if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends SearchForm {
    constructor() {
      super();
      this.cachedResults = {};
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
      this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
      this.isOpen = false;
      this.abortController = null;
      this.searchTerm = '';
      this.escapeHandled = false;

      this.setupEventListeners();
    }

    setupEventListeners() {
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.addEventListener('focusout', this.onFocusOut.bind(this));
      this.addEventListener('keyup', this.onKeyup.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      this.addEventListener('focusin', this.onOptionFocus.bind(this));
    }

    // A result link reached with Tab becomes the current option: one highlight, and ArrowUp/Down in the field
    // continue from it.
    onOptionFocus(event) {
      const option = event.target;
      if (option === this.input || option.getAttribute?.('role') !== 'option') return;
      this.querySelectorAll('[role="option"][aria-selected="true"]').forEach((element) => {
        if (element !== option) element.setAttribute('aria-selected', false);
      });
      option.setAttribute('aria-selected', true);
      this.input.setAttribute('aria-activedescendant', option.id);
    }

    // Don't clear the field while a result is selected (Dawn's rule, for the option-on-link markup).
    shouldResetForm() {
      return !this.querySelector('a[aria-selected="true"], [aria-selected="true"] a');
    }

    getQuery() {
      return this.input.value.trim();
    }

    static cacheKey(term) {
      return term.toLowerCase().replace(/\s+/g, ' ');
    }

    onChange() {
      super.onChange();
      const newSearchTerm = this.getQuery();
      if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
        // Remove results that no longer fit the new term so they don't flash when the dropdown reopens
        this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
      }

      this.updateSearchForTerm(this.searchTerm, newSearchTerm);
      this.searchTerm = newSearchTerm;

      if (!this.searchTerm.length) {
        this.abortPending();
        this.close(true);
        return;
      }

      this.getSearchResults(this.searchTerm);
    }

    onFormSubmit(event) {
      // A selected link option navigates itself (selectOption); the "Search for" button option submits the form.
      if (!this.getQuery().length || this.querySelector('a[aria-selected="true"], [aria-selected="true"] a')) {
        event.preventDefault();
      }
    }

    onFormReset(event) {
      super.onFormReset(event);
      if (this.shouldResetForm()) {
        this.searchTerm = '';
        this.abortPending();
        this.closeResults(true);
      }
    }

    onFocus() {
      const currentSearchTerm = this.getQuery();

      if (!currentSearchTerm.length) return;

      if (this.searchTerm !== currentSearchTerm) {
        // Search term was changed from another search input, treat it as a user change
        this.onChange();
      } else if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getSearchResults(this.searchTerm);
      }
    }

    onFocusOut() {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      });
    }

    onKeyup(event) {
      if (event.code === 'Escape' || event.key === 'Escape') {
        // The dropdown consumed this Escape; keep a surrounding search modal open.
        if (this.escapeHandled) event.stopPropagation();
        this.escapeHandled = false;
        return;
      }

      // Keys on a focused result link or button keep their native behaviour (Enter follows that link only).
      if (event.target !== this.input) return;

      if (!this.getQuery().length) this.close(true);
      event.preventDefault();

      switch (event.code) {
        case 'ArrowUp':
          this.switchOption('up');
          break;
        case 'ArrowDown':
          this.switchOption('down');
          break;
        case 'Enter':
          this.selectOption();
          break;
      }
    }

    onKeydown(event) {
      // Prevent the cursor from moving in the input when using the up and down arrow keys (on a focused result
      // link the arrows keep scrolling the panel)
      if (event.target === this.input && (event.code === 'ArrowUp' || event.code === 'ArrowDown')) {
        event.preventDefault();
      }

      if ((event.code === 'Escape' || event.key === 'Escape') && this.getAttribute('open')) {
        event.preventDefault();
        event.stopPropagation();
        this.escapeHandled = true;
        this.close();
        this.input.focus();
      }
    }

    updateSearchForTerm(previousTerm, newTerm) {
      const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
      const currentButtonText = searchForTextElement?.innerText;
      if (!currentButtonText || !previousTerm) return;

      const escapedTerm = previousTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const matches = currentButtonText.match(new RegExp(escapedTerm, 'g'));
      // The term also appears in the surrounding text: don't guess which part to replace
      if (!matches || matches.length > 1) return;
      searchForTextElement.innerText = currentButtonText.replace(previousTerm, newTerm);
    }

    switchOption(direction) {
      if (!this.getAttribute('open')) return;

      const moveUp = direction === 'up';
      const selectedElement = this.querySelector('[aria-selected="true"]');

      // Only visible options (pages/articles are rendered twice for mobile and desktop). The option role sits on
      // the result link / "Search for" button itself (sections/predictive-search.liquid).
      const allVisibleElements = Array.from(this.querySelectorAll('[role="option"]')).filter(
        (element) => element.offsetParent !== null
      );
      if (!allVisibleElements.length) return;
      if (moveUp && !selectedElement) return;

      const selectedElementIndex = allVisibleElements.indexOf(selectedElement);
      let activeElementIndex = 0;

      if (this.statusElement) this.statusElement.textContent = '';

      if (!moveUp && selectedElement) {
        activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
      } else if (moveUp) {
        activeElementIndex = selectedElementIndex <= 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
      }

      if (activeElementIndex === selectedElementIndex) return;

      const activeElement = allVisibleElements[activeElementIndex];

      activeElement.setAttribute('aria-selected', true);
      if (selectedElement) selectedElement.setAttribute('aria-selected', false);

      this.input.setAttribute('aria-activedescendant', activeElement.id);
      activeElement.scrollIntoView({ block: 'nearest' });
    }

    selectOption() {
      const selected = this.querySelector('[role="option"][aria-selected="true"]');
      const selectedOption = selected?.matches('a, button') ? selected : selected?.querySelector('a, button');

      if (selectedOption) selectedOption.click();
    }

    abortPending() {
      this.abortController?.abort();
      this.abortController = null;
    }

    getSearchResults(searchTerm) {
      const queryKey = PredictiveSearch.cacheKey(searchTerm);
      this.setLiveRegionLoadingState();

      if (this.cachedResults[queryKey]) {
        this.abortPending();
        this.renderSearchResults(this.cachedResults[queryKey]);
        const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
        return;
      }

      const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);

      // Only the latest query may render: cancel the previous request.
      this.abortPending();
      const controller = new AbortController();
      this.abortController = controller;

      fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
        signal: controller.signal,
      })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .then((text) => {
          const section = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#shopify-section-predictive-search');
          if (!section) throw new Error('Unexpected response');
          const resultsMarkup = section.innerHTML;

          // Keep the cache in all instances in sync
          this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          });

          // Stale response: the input has changed in the meantime
          if (controller.signal.aborted || PredictiveSearch.cacheKey(this.getQuery()) !== queryKey) return;

          this.renderSearchResults(resultsMarkup);
          searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
        })
        .catch((error) => {
          searchDeferred?.reject(error);
          if (error?.name === 'AbortError') return;
          // Fail quietly: the search form still submits to the full search page.
          this.removeAttribute('loading');
          this.close();
        })
        .finally(() => {
          if (this.abortController === controller) this.abortController = null;
        });
    }

    getTotalResultCount() {
      return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
    }

    dispatchSearchUpdateEvent(query) {
      const { SearchUpdateEvent } = window.StandardEvents || {};
      if (!SearchUpdateEvent) return null;

      const deferred = SearchUpdateEvent.createPromise();
      this.dispatchEvent(
        new SearchUpdateEvent({
          search: { query },
          promise: deferred.promise,
        })
      );
      return deferred;
    }

    setLiveRegionLoadingState() {
      this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
      this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

      this.setLiveRegionText(this.loadingText);
      this.setAttribute('loading', true);
    }

    setLiveRegionText(statusText) {
      if (!this.statusElement) return;
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = statusText;

      clearTimeout(this.liveRegionTimer);
      this.liveRegionTimer = setTimeout(() => {
        this.statusElement.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    renderSearchResults(resultsMarkup) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
      this.setAttribute('results', true);

      this.setLiveRegionResults();
      this.open();
    }

    setLiveRegionResults() {
      this.removeAttribute('loading');
      const countElement = this.querySelector('[data-predictive-search-live-region-count-value]');
      if (countElement) this.setLiveRegionText(countElement.textContent.trim());
    }

    getResultsMaxHeight() {
      const headerBottom = document.querySelector('.section-header')?.getBoundingClientRect().bottom || 0;
      this.resultsMaxHeight = window.innerHeight - headerBottom;
      return this.resultsMaxHeight;
    }

    open() {
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
      this.setAttribute('open', true);
      this.input.setAttribute('aria-expanded', true);
      this.isOpen = true;
    }

    close(clearSearchTerm = false) {
      this.closeResults(clearSearchTerm);
      this.isOpen = false;
    }

    closeResults(clearSearchTerm = false) {
      if (clearSearchTerm) {
        this.input.value = '';
        this.removeAttribute('results');
      }
      const selected = this.querySelector('[aria-selected="true"]');

      if (selected) selected.setAttribute('aria-selected', false);

      this.input.removeAttribute('aria-activedescendant');
      this.removeAttribute('loading');
      this.removeAttribute('open');
      this.input.setAttribute('aria-expanded', false);
      this.resultsMaxHeight = false;
      this.predictiveSearchResults.removeAttribute('style');
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
